import jsPDF from "jspdf";
import "jspdf-autotable";
import { EXPORT_FILE_NAMES, TABLE_HEADERS } from "./constants";

/**
 * Utility functions for price update operations
 * Provides pure functions for data transformation, validation, and export
 */

/**
 * Get current date time formatted as DD/MM/YYYY
 * @returns {string} Formatted date string
 */
export const getCurrentDateTime = () => {
  const now = new Date();
  return `${String(now.getDate()).padStart(2, "0")}/${String(
    now.getMonth() + 1,
  ).padStart(2, "0")}/${now.getFullYear()}`;
};

/**
 * Validate that offer price is not greater than regular price
 * @param {string} productId - Product ID
 * @param {string} variantId - Variant ID (optional)
 * @param {Object} updatedPrices - Updated prices object
 * @param {Array} products - Products array
 * @param {Function} setPriceErrors - State setter for price errors
 */
export const validatePrices = (
  productId,
  variantId,
  updatedPrices,
  products,
  setPriceErrors,
) => {
  const errorKey = variantId ? `${productId}-${variantId}` : productId;

  const product = products.find((p) => p._id === productId);
  if (!product) return;

  let currentPrice, currentOfferPrice, newPrice, newOfferPrice;

  if (variantId) {
    const variant = product.variants?.find((v) => v._id === variantId);
    currentPrice = variant?.price || 0;
    currentOfferPrice = variant?.offerPrice || 0;
    newPrice = updatedPrices[productId]?.[variantId]?.newPrice;
    newOfferPrice = updatedPrices[productId]?.[variantId]?.newOfferPrice;
  } else {
    currentPrice = product.price || 0;
    currentOfferPrice = product.offerPrice || 0;
    newPrice = updatedPrices[productId]?.newPrice;
    newOfferPrice = updatedPrices[productId]?.newOfferPrice;
  }

  const effectivePrice = newPrice ? parseFloat(newPrice) : currentPrice;
  const effectiveOfferPrice = newOfferPrice
    ? parseFloat(newOfferPrice)
    : newPrice
      ? null
      : currentOfferPrice;

  if (effectiveOfferPrice && effectiveOfferPrice > effectivePrice) {
    setPriceErrors((prev) => ({ ...prev, [errorKey]: true }));
  } else {
    setPriceErrors((prev) => {
      const updated = { ...prev };
      delete updated[errorKey];
      return updated;
    });
  }
};

/**
 * Build updates array from edited prices
 * @param {Object} editedPrices - Edited prices object
 * @returns {Array} Array of update objects
 */
export const buildUpdatesArray = (editedPrices) => {
  const updates = [];

  Object.entries(editedPrices).forEach(([productId, value]) => {
    // Product without variants
    if ("newPrice" in value || "newOfferPrice" in value) {
      const updateData = { productId };

      // New Price
      if (value.newPrice !== undefined && value.newPrice !== "") {
        updateData.newPrice = value.newPrice;
      }

      // IMPORTANT: Allow null for removal
      if ("newOfferPrice" in value) {
        updateData.newOfferPrice = value.newOfferPrice;
      }

      if (
        updateData.newPrice !== undefined ||
        updateData.newOfferPrice !== undefined
      ) {
        updates.push(updateData);
      }
    }

    // Product with variants
    else {
      Object.entries(value).forEach(([variantId, variantData]) => {
        const updateData = { productId, variantId };

        if (
          variantData.newPrice !== undefined &&
          variantData.newPrice !== ""
        ) {
          updateData.newPrice = variantData.newPrice;
        }

        // IMPORTANT: Allow null for removal
        if ("newOfferPrice" in variantData) {
          updateData.newOfferPrice = variantData.newOfferPrice;
        }

        if (
          updateData.newPrice !== undefined ||
          updateData.newOfferPrice !== undefined
        ) {
          updates.push(updateData);
        }
      });
    }
  });

  return updates;
};


/**
 * Export price changes to PDF
 * @param {Object} editedPrices - Edited prices object
 * @param {Array} products - Products array
 */
export const exportChanges = (editedPrices, products) => {
  const doc = new jsPDF();
  doc.text(`Price Change - ${getCurrentDateTime()}`, 10, 10);

  const tableData = Object.keys(editedPrices)
    .map((productId) => {
      const product = products.find((p) => p._id === productId);
      if (!product) return null;

      const oldPrice = product.price || "N/A";
      const oldOfferPrice = product.offerPrice || "N/A";
      const newPrice = editedPrices[productId]?.newPrice || "";
      const newOfferPrice = editedPrices[productId]?.newOfferPrice || "";

      return [
        product.productNumber || "",
        product.productName || "",
        product.categoryName || "",
        product.subcategoryName || "",
        oldPrice,
        oldOfferPrice,
        newPrice,
        newOfferPrice,
      ];
    })
    .filter(Boolean);

  doc.autoTable({
    startY: 20,
    head: [
      [
        "Product Number",
        TABLE_HEADERS.PRODUCT_NAME,
        TABLE_HEADERS.CATEGORY,
        TABLE_HEADERS.SUBCATEGORY,
        "Old Price",
        "Old Offer Price",
        TABLE_HEADERS.NEW_PRICE,
        TABLE_HEADERS.NEW_OFFER_PRICE,
      ],
    ],
    body: tableData,
  });

  doc.save(EXPORT_FILE_NAMES.PRICE_CHANGES);
};

/**
 * Export all prices to PDF
 * @param {Array} allProducts - All products array
 */
export const exportAllPrices = (allProducts) => {
  const doc = new jsPDF();
  doc.text(`All Prices - ${getCurrentDateTime()}`, 10, 10);

  const tableData = allProducts.flatMap((product) => {
    if (product.variants?.length > 0) {
      return product.variants.map((variant) => [
        product.productNumber || "",
        product.productName || "",
        product.categoryName || "",
        product.subcategoryName || "",
        variant.price || "",
        variant.offerPrice || "",
      ]);
    } else {
      return [
        [
          product.productNumber || "",
          product.productName || "",
          product.categoryName || "",
          product.subcategoryName || "",
          product.price || "",
          product.offerPrice || "",
        ],
      ];
    }
  });

  doc.autoTable({
    startY: 20,
    head: [
      [
        "Product Number",
        TABLE_HEADERS.PRODUCT_NAME,
        TABLE_HEADERS.CATEGORY,
        TABLE_HEADERS.SUBCATEGORY,
        "Price",
        "Offer Price",
      ],
    ],
    body: tableData,
  });

  doc.save(EXPORT_FILE_NAMES.ALL_PRICES);
};

/**
 * Transform products into flat rows for table display
 * Handles both products with variants and without variants
 * @param {Array} products - Products array
 * @param {Object} editedPrices - Edited prices object
 * @returns {Array} Flattened product rows
 */
export const transformProductsToRows = (products, editedPrices) => {
  return products.flatMap((product) =>
    product.variants.length > 0
      ? product.variants.map((variant) => ({
        _id: product._id,
        variantId: variant._id,
        productName: `${product.productName}`,
        categoryName: product.categoryName,
        subcategoryName: product.subcategoryName,
        price: variant.price,
        offerPrice: variant.offerPrice,
        isVisible: product.isVisible,
        newPrice: editedPrices[product._id]?.[variant._id]?.newPrice || "",
        newOfferPrice:
          editedPrices[product._id]?.[variant._id]?.newOfferPrice || "",
      }))
      : [
        {
          _id: product._id,
          productName: product.productName,
          categoryName: product.categoryName,
          subcategoryName: product.subcategoryName,
          price: product.price,
          offerPrice: product.offerPrice,
          isVisible: product.isVisible,
          newPrice: editedPrices[product._id]?.newPrice || "",
          newOfferPrice: editedPrices[product._id]?.newOfferPrice || "",
        },
      ],
  );
};

/**
 * Paginate data array
 * @param {Array} data - Data to paginate
 * @param {number} page - Current page (1-indexed)
 * @param {number} rowsPerPage - Rows per page
 * @returns {Array} Paginated data
 */
export const paginateData = (data, page, rowsPerPage) => {
  return data.slice((page - 1) * rowsPerPage, page * rowsPerPage);
};